import { useState, useEffect, useRef } from "react";
import { commentsAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAdmin } from "../lib/roles";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Trash, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function Comments({ entityType, entityId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();
  const bottomRef = useRef(null);

  const fetchComments = async () => {
    if (!entityId) return;
    try {
      const { data } = await commentsAPI.list(entityType, entityId);
      setComments(data.comments);
    } catch (e) {
      console.warn(`Failed to load comments for ${entityType}/${entityId}:`, e);
    }
  };

  useEffect(() => { fetchComments(); }, [entityType, entityId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length]);

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await commentsAPI.create({ entity_type: entityType, entity_id: entityId, text: text.trim() });
      setComments((prev) => [...prev, data]);
      setText("");
    } catch (e) { toast.error("Failed to post comment"); }
    setPosting(false);
  };

  const handleDelete = async (id) => {
    try {
      await commentsAPI.delete(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="comments-section">
      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">
        Comments {comments.length > 0 && <span className="text-zinc-300">({comments.length})</span>}
      </p>

      {/* Comment list */}
      {comments.length > 0 ? (
        <div className="space-y-2 max-h-48 overflow-y-auto mb-2">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2 group" data-testid={`comment-${c.id}`}>
              <div className="w-6 h-6 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mt-0.5">
                {c.author_name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-zinc-700">{c.author_name}</span>
                  <span className="text-[10px] text-zinc-300">{timeAgo(c.created_at)}</span>
                  {(c.author_id === user?.id || isAdmin(user)) && (
                    <button onClick={() => handleDelete(c.id)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto" data-testid={`delete-comment-${c.id}`}>
                      <Trash size={10} className="text-zinc-300 hover:text-red-400" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-zinc-600 leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      ) : (
        <p className="text-[11px] text-zinc-300 mb-2">No comments yet</p>
      )}

      {/* Input */}
      <div className="flex gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
          placeholder="Add a comment..."
          className="rounded-sm text-xs h-8 min-h-[32px] max-h-20 resize-none py-1.5"
          data-testid="comment-input"
        />
        <Button size="sm" onClick={handlePost} disabled={posting || !text.trim()} data-testid="post-comment-btn"
          className="h-8 px-2 bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm flex-shrink-0">
          <PaperPlaneRight size={12} />
        </Button>
      </div>
    </div>
  );
}
